import { clientEnv } from "@/config";
import { apiRoutes } from "@/routes/routes";
import { IGeneralData, ITokenBase } from "@/interfaces";
import { checkIsToken, fetcher } from "@/lib";
import {
  IBankValidationMutationPayload,
  ICreditScoreSendOtpMutationPayload,
  ICreditScoreVerifyOtpMutationPayload,
  IRawBankValidationMutationResData,
  IRawCreditScoreOtpMutationResData,
} from "@/interfaces/inquiries/creditScore";
import {
  ICheckMutationResData,
  IChequeInquiryMutationPayload,
  IChequeTransfersChainInquiryMutationPayload,
  IChequeTransfersChainInquiryMutationResData,
  IRegisterCheckMutationResData,
  IRegisterChequeMutationPayload,
} from "@/interfaces/inquiries/chequeInquiry";
import {
  IBackChequesAuthorizeMutationResData,
  IBackChequesAuthorizePayload,
  IBackChequesMutationResData,
  IBackChequesPayload,
  IBackChequesVerifyMutationResData,
  IBackChequesVerifyPayload,
  IActiveLoansInquiryMutationPayload,
  IBouncedCheckInquiryMutationPayload,
  ICardsInquiryMutationPayload,
  ICardToDepositInquiryMutationPayload,
  ICardToIbanInquiryMutationPayload,
  IDepositToIbanInquiryMutationPayload,
  IIbansInquiryMutationPayload,
  IRawBouncedCheckInquiryMutationResData,
  IRawActiveLoansInquiryMutationResData,
  IRawCardsInquiryMutationResData,
  IRawCardToDepositInquiryMutationResData,
  IRawCardToIbanInquiryMutationResData,
  IRawDepositToIbanInquiryMutationResData,
  IRawIbansInquiryMutationResData,
} from "@/interfaces/inquiries/bank";
import {
  ICompanyInfoInquiryMutationPayload,
  ICompanyNewspaperMutationPayload,
  ICompanyNewspaperMutationResData,
  ICorporateIdentityInquiryMutationPayload,
  IRawCompanyChildrenInquiryMutationResData,
  IRawCompanyInfoInquiryMutationResData,
  IRawCompanyLicensesInquiryMutationResData,
  IRawCompanyMembersInquiryMutationResData,
  IRawCompanyParentsInquiryMutationResData,
  IRawCorporateIdentityInquiryMutationResData,
  IRawTaxRecordsInquiryMutationResData,
  ITaxRecordsInquiryMutationPayload,
} from "@/interfaces/inquiries/legal";
import {
  IBillPayload,
  IElectricityBillImagePayload,
  IElectricityBillInfoInquiryMutationResData,
  IElectricityBillInquiryMutationResData,
  IForeignersInquiryMutationPayload,
  IGasBillHistoryInquiryMutationResData,
  IGasBillInfoInquiryMutationResData,
  IGasBillInfoPayload,
  IGasBillInquiryMutationResData,
  IGasBillPayload,
  IIdentityInquiryMutationPayload,
  IMilitaryServiceInquiryMutationPayload,
  IMobileBillPayload,
  IMtnBillInquiryMutationResData,
  IPassportStatusInquiryMutationPayload,
  IPostalCodeInquiryMutationPayload,
  IPostalTrackingMutationPayload,
  IPostalTrackingMutationResData,
  IRawEducationalExemptionInquiryMutationResData,
  IRawForeignersInquiryMutationResData,
  IRawIdentityInquiryMutationResData,
  IRawMilitaryCardInquiryMutationResData,
  IRawMilitaryServiceInquiryMutationResData,
  IRawPassportStatusInquiryMutationResData,
  IRawPostalCodeInquiryMutationResData,
  IRawPurchasedElectricityHistoryInquiryResData,
  IRawPurchasedElectricityImageInquiryResData,
  IRawSanaInquiryMutationResData,
  ISanaInquiryMutationPayload,
  IWaterBillInfoInquiryMutationResData,
  IWaterBillInquiryMutationResData,
} from "@/interfaces/inquiries/governmental";
import {
  ICardDocumentMutationPayload,
  IFreewayTollInquiryPayload,
  IMotorcycleFinesMutationPayload,
  IDrivingLicenseStatusInquiryMutationPayload,
  INegativePointsInquiryMutationPayload,
  IPlateInquiryMutationPayload,
  IRawCarDocumentMutationResData,
  IRawFreewayTollInquiryResData,
  IRawMotorcycleFinesMutationResData,
  IRawNegativePointsInquiryMutationResData,
  IRawDrivingLicenseStatusInquiryMutationResData,
  IRawPlateInquiryMutationResData,
  IRawTrafficFinesMutationResData,
  IRawTrafficFinesPhotoMutationResData,
  IRawPurchasedThirdPartyInsuranceInquiryResData,
  IRawVehicleInfoInquiryResData,
  IThirdPartyInsuranceInquiryPayload,
  ITrafficFinesMutationPayload,
  ITrafficFinesPhotoMutationPayload,
  IRawVehicleTransferTaxDropResData,
  IRawVehicleTransferTaxInquiryResData,
  IRawVehicleTransferTaxPayResData,
  IRawVehicleTransferTaxPrintResData,
  IVehicleInfoInquiryPayload,
  IVehicleTransferTaxInquiryPayload,
  IVehicleTransferTaxPayPayload,
  IVehicleTransferTaxPrintPayload,
} from "@/interfaces/inquiries/vehicle";

class InquiryClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async ibansInquiryMutation({
    payload,
    token,
  }: {
    payload: IIbansInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawIbansInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.IBANS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawIbansInquiryMutationResData>,
      IIbansInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async cardsInquiryMutation({
    payload,
    token,
  }: {
    payload: ICardsInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawCardsInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CARDS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCardsInquiryMutationResData>,
      ICardsInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async cardToIbanInquiryMutation({
    payload,
    token,
  }: {
    payload: ICardToIbanInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawCardToIbanInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CARD_TO_IBAN_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCardToIbanInquiryMutationResData>,
      ICardToIbanInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async cardToDepositInquiryMutation({
    payload,
    token,
  }: {
    payload: ICardToDepositInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawCardToDepositInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CARD_TO_DEPOSIT_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCardToDepositInquiryMutationResData>,
      ICardToDepositInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async depositToIbanInquiryMutation({
    payload,
    token,
  }: {
    payload: IDepositToIbanInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawDepositToIbanInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.DEPOSIT_TO_IBAN_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawDepositToIbanInquiryMutationResData>,
      IDepositToIbanInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async bouncedCheckInquiryMutation({
    payload,
    token,
  }: {
    payload: IBouncedCheckInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawBouncedCheckInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.BOUNCED_CHECK_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawBouncedCheckInquiryMutationResData>,
      IBouncedCheckInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async activeLoansInquiryMutation({
    payload,
    token,
  }: {
    payload: IActiveLoansInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawActiveLoansInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.ACTIVE_LOANS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawActiveLoansInquiryMutationResData>,
      IActiveLoansInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async taxRecordsInquiryMutation({
    payload,
    token,
  }: {
    payload: ITaxRecordsInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawTaxRecordsInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TAX_RECORDS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawTaxRecordsInquiryMutationResData>,
      ITaxRecordsInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async bouncedCheckDetailsInquiryMutation({
    payload,
    token,
  }: {
    payload: IBouncedCheckInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawBouncedCheckInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.BOUNCED_CHECK_DETAILS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawBouncedCheckInquiryMutationResData>,
      IBouncedCheckInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async backChequesAuthorizeMutation({
    payload,
    token,
  }: {
    payload: IBackChequesAuthorizePayload;
  } & ITokenBase): Promise<IGeneralData<IBackChequesAuthorizeMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.BACK_CHEQUES_AUTHORIZE;
    const data = await fetcher<
      IGeneralData<IBackChequesAuthorizeMutationResData>,
      IBackChequesAuthorizePayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async backChequesVerifyMutation({
    payload,
    token,
  }: {
    payload: IBackChequesVerifyPayload;
  } & ITokenBase): Promise<IGeneralData<IBackChequesVerifyMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.BACK_CHEQUES_VERIFY;
    const data = await fetcher<
      IGeneralData<IBackChequesVerifyMutationResData>,
      IBackChequesVerifyPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async backChequesMutation({
    payload,
    token,
  }: {
    payload: IBackChequesPayload;
  } & ITokenBase): Promise<IGeneralData<IBackChequesMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.BACK_CHEQUES;
    const data = await fetcher<
      IGeneralData<IBackChequesMutationResData>,
      IBackChequesPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async companyInfoInquiryMutation({
    payload,
    token,
  }: {
    payload: ICompanyInfoInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawCompanyInfoInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.COMPANY_INFO_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCompanyInfoInquiryMutationResData>,
      ICompanyInfoInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async companyMemberInquiryMutation({
    payload,
    token,
  }: {
    payload: ICompanyInfoInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawCompanyMembersInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.COMPANY_MEMBERS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCompanyMembersInquiryMutationResData>,
      ICompanyInfoInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async companyLicensesInquiryMutation({
    payload,
    token,
  }: {
    payload: ICompanyInfoInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawCompanyLicensesInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.COMPANY_LICENSES_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCompanyLicensesInquiryMutationResData>,
      ICompanyInfoInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async companyParentsInquiryMutation({
    payload,
    token,
  }: {
    payload: ICompanyInfoInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawCompanyParentsInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.COMPANY_PARENT_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCompanyParentsInquiryMutationResData>,
      ICompanyInfoInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async companyChildrenInquiryMutation({
    payload,
    token,
  }: {
    payload: ICompanyInfoInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawCompanyChildrenInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.COMPANY_CHILDREN_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCompanyChildrenInquiryMutationResData>,
      ICompanyInfoInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async militaryServiceInquiryMutation({
    payload,
    token,
  }: {
    payload: IMilitaryServiceInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawMilitaryServiceInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.MILITARY_SERVICE_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawMilitaryServiceInquiryMutationResData>,
      IMilitaryServiceInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async militaryCardInquiryMutation({
    payload,
    token,
  }: {
    payload: IMilitaryServiceInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawMilitaryCardInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.MILITARY_CARD_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawMilitaryCardInquiryMutationResData>,
      IMilitaryServiceInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async educationalExemptionInquiryMutation({
    payload,
    token,
  }: {
    payload: IMilitaryServiceInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawEducationalExemptionInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.EDUCATIONAL_EXEMPTION_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawEducationalExemptionInquiryMutationResData>,
      IMilitaryServiceInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async postalCodeInquiryMutation({
    payload,
    token,
  }: {
    payload: IPostalCodeInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawPostalCodeInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.POSTAL_CODE_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawPostalCodeInquiryMutationResData>,
      IPostalCodeInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async trafficFinesMutation({
    payload,
    token,
  }: {
    payload: ITrafficFinesMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawTrafficFinesMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TRAFFIC_FINES;
    const data = await fetcher<
      IGeneralData<IRawTrafficFinesMutationResData>,
      ITrafficFinesMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async vehicleTransferTaxInquiryMutation({
    payload,
    token,
  }: {
    payload: IVehicleTransferTaxInquiryPayload;
  } & ITokenBase): Promise<IGeneralData<IRawVehicleTransferTaxInquiryResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TAX_VEHICLE_GET;
    const data = await fetcher<
      IGeneralData<IRawVehicleTransferTaxInquiryResData>,
      IVehicleTransferTaxInquiryPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async freewayTollInquiryMutation({
    payload,
    token,
  }: {
    payload: IFreewayTollInquiryPayload;
  } & ITokenBase): Promise<IGeneralData<IRawFreewayTollInquiryResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.FREEWAY_TOLL;
    const data = await fetcher<
      IGeneralData<IRawFreewayTollInquiryResData>,
      IFreewayTollInquiryPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async vehicleInfoInquiryMutation({
    payload,
    token,
  }: {
    payload: IVehicleInfoInquiryPayload;
  } & ITokenBase): Promise<IGeneralData<IRawVehicleInfoInquiryResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.VEHICLE_INFO;
    const data = await fetcher<
      IGeneralData<IRawVehicleInfoInquiryResData>,
      IVehicleInfoInquiryPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async thirdPartyInsuranceInquiryMutation({
    payload,
    token,
  }: {
    payload: IThirdPartyInsuranceInquiryPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawPurchasedThirdPartyInsuranceInquiryResData["data"]["data"]>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.THIRD_PARTY_INSURANCE;
    const data = await fetcher<
      IGeneralData<IRawPurchasedThirdPartyInsuranceInquiryResData["data"]["data"]>,
      IThirdPartyInsuranceInquiryPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async vehicleTransferTaxPayMutation({
    payload,
    token,
  }: {
    payload: IVehicleTransferTaxPayPayload;
  } & ITokenBase): Promise<IGeneralData<IRawVehicleTransferTaxPayResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TAX_VEHICLE_PAY;
    const data = await fetcher<
      IGeneralData<IRawVehicleTransferTaxPayResData>,
      IVehicleTransferTaxPayPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async vehicleTransferTaxDropMutation({
    payload,
    token,
  }: {
    payload: IVehicleTransferTaxPayPayload;
  } & ITokenBase): Promise<IGeneralData<IRawVehicleTransferTaxDropResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TAX_VEHICLE_DROP;
    const data = await fetcher<
      IGeneralData<IRawVehicleTransferTaxDropResData>,
      IVehicleTransferTaxPayPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async vehicleTransferTaxPrintMutation({
    payload,
    token,
  }: {
    payload: IVehicleTransferTaxPrintPayload;
  } & ITokenBase): Promise<IGeneralData<IRawVehicleTransferTaxPrintResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TAX_VEHICLE_PRINT;
    const data = await fetcher<
      IGeneralData<IRawVehicleTransferTaxPrintResData>,
      IVehicleTransferTaxPrintPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async trafficFinesPhotoMutation({
    payload,
    token,
  }: {
    payload: ITrafficFinesPhotoMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawTrafficFinesPhotoMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TRAFFIC_FINES_PHOTO;
    const data = await fetcher<
      IGeneralData<IRawTrafficFinesPhotoMutationResData>,
      ITrafficFinesPhotoMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async motorcycleFinesMutation({
    payload,
    token,
  }: {
    payload: IMotorcycleFinesMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawMotorcycleFinesMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.MOTORCYCLE_FINES;
    const data = await fetcher<
      IGeneralData<IRawMotorcycleFinesMutationResData>,
      IMotorcycleFinesMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async plateInquiryMutation({
    payload,
    token,
  }: {
    payload: IPlateInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawPlateInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.PLATE_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawPlateInquiryMutationResData>,
      IPlateInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async negativePointsInquiryMutation({
    payload,
    token,
  }: {
    payload: INegativePointsInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawNegativePointsInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.NEGATIVE_POINTS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawNegativePointsInquiryMutationResData>,
      INegativePointsInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async drivingLicenseStatusInquiryMutation({
    payload,
    token,
  }: {
    payload: IDrivingLicenseStatusInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawDrivingLicenseStatusInquiryMutationResData>
  > {
    checkIsToken(token);
    return fetcher<
      IGeneralData<IRawDrivingLicenseStatusInquiryMutationResData>,
      IDrivingLicenseStatusInquiryMutationPayload
    >({
      url: apiRoutes.inquiry.DRIVING_LICENSE_STATUS_INQUIRY,
      payload,
      token,
      method: "POST",
    });
  }

  async passportStatusInquiryMutation({
    payload,
    token,
  }: {
    payload: IPassportStatusInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawPassportStatusInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.PASSPORT_STATUS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawPassportStatusInquiryMutationResData>,
      IPassportStatusInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async sanaInquiryMutation({
    payload,
    token,
  }: {
    payload: ISanaInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawSanaInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.SANA_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawSanaInquiryMutationResData>,
      ISanaInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async identityInquiryMutation({
    payload,
    token,
  }: {
    payload: IIdentityInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawIdentityInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.IDENTITY_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawIdentityInquiryMutationResData>,
      IIdentityInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async bankValidationMutation({
    payload,
    token,
  }: {
    payload: IBankValidationMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawBankValidationMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.BANK_VALIDATION;
    const data = await fetcher<
      IGeneralData<IRawBankValidationMutationResData>,
      IBankValidationMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async internalBankValidationMutation({
    payload,
    token,
  }: {
    payload: IBankValidationMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawBankValidationMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.INTERNAL_BANK_VALIDATION;
    const data = await fetcher<
      IGeneralData<IRawBankValidationMutationResData>,
      IBankValidationMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async revalidateBankValidationMutation({
    payload,
    token,
  }: {
    payload: IBankValidationMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawBankValidationMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.BANK_REVALIDATION;
    const data = await fetcher<
      IGeneralData<IRawBankValidationMutationResData>,
      IBankValidationMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async creditScoreSendOtpMutation({
    payload,
    token,
  }: {
    payload: ICreditScoreSendOtpMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawCreditScoreOtpMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CREDIT_SCORE_SEND_OTP;
    const data = await fetcher<
      IGeneralData<IRawCreditScoreOtpMutationResData>,
      ICreditScoreSendOtpMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async internalCreditScoreSendOtpMutation({
    payload,
    token,
  }: {
    payload: ICreditScoreSendOtpMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawCreditScoreOtpMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.INTERNAL_CREDIT_SCORE_SEND_OTP;
    const data = await fetcher<
      IGeneralData<IRawCreditScoreOtpMutationResData>,
      ICreditScoreSendOtpMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async internalCreditScoreVerifyOtpMutation({
    payload,
    token,
  }: {
    payload: ICreditScoreVerifyOtpMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawCreditScoreOtpMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.INTERNAL_CREDIT_SCORE_VERIFY_OTP;
    const data = await fetcher<
      IGeneralData<IRawCreditScoreOtpMutationResData>,
      ICreditScoreVerifyOtpMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async creditScoreVerifyOtpMutation({
    payload,
    token,
  }: {
    payload: ICreditScoreVerifyOtpMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawCreditScoreOtpMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CREDIT_SCORE_VERIFY_OTP;
    const data = await fetcher<
      IGeneralData<IRawCreditScoreOtpMutationResData>,
      ICreditScoreVerifyOtpMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async foreignersInquiryMutation({
    payload,
    token,
  }: {
    payload: IForeignersInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawForeignersInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.FOREIGNERS_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawForeignersInquiryMutationResData>,
      IForeignersInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async corporateIdentityInquiryMutation({
    payload,
    token,
  }: {
    payload: ICorporateIdentityInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawCorporateIdentityInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CORPORATION_IDENTITY;
    const data = await fetcher<
      IGeneralData<IRawCorporateIdentityInquiryMutationResData>,
      ICorporateIdentityInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async carDocumentInquiryMutation({
    payload,
    token,
  }: {
    payload: ICardDocumentMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRawCarDocumentMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CAR_DOCUMENT_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRawCarDocumentMutationResData>,
      ICardDocumentMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async registerChequeInquiryMutation({
    payload,
    token,
  }: {
    payload: IRegisterChequeMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IRegisterCheckMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CHEQUE_INQUIRY;
    const data = await fetcher<
      IGeneralData<IRegisterCheckMutationResData>,
      IRegisterChequeMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async chequeInquiryMutation({
    payload,
    token,
  }: {
    payload: IChequeInquiryMutationPayload;
  } & ITokenBase): Promise<IGeneralData<ICheckMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.CHEQUE_V2_INQUIRY;
    const data = await fetcher<
      IGeneralData<ICheckMutationResData>,
      IChequeInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async chequeTransfersChainInquiryMutation({
    payload,
    token,
  }: {
    payload: IChequeTransfersChainInquiryMutationPayload;
  } & ITokenBase): Promise<
    IGeneralData<IChequeTransfersChainInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.TRANSFER_CHAIN_INQUIRY;
    const data = await fetcher<
      IGeneralData<IChequeTransfersChainInquiryMutationResData>,
      IChequeTransfersChainInquiryMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async postalTrackingMutation({
    payload,
    token,
  }: {
    payload: IPostalTrackingMutationPayload;
  } & ITokenBase): Promise<IGeneralData<IPostalTrackingMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.POSTAL_TRACKING_INQUIRY;
    const data = await fetcher<
      IGeneralData<IPostalTrackingMutationResData>,
      IPostalTrackingMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async companyNewspapersMutation({
    payload,
    token,
  }: {
    payload: ICompanyNewspaperMutationPayload;
  } & ITokenBase): Promise<IGeneralData<ICompanyNewspaperMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.COMPANY_NEWSPAPERS_INQUIRY;
    const data = await fetcher<
      IGeneralData<ICompanyNewspaperMutationResData>,
      ICompanyNewspaperMutationPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async electricityBillMutation({
    payload,
    token,
  }: {
    payload: IBillPayload;
  } & ITokenBase): Promise<
    IGeneralData<IElectricityBillInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.ELECTRICITY_BILL_INQUIRY;
    const data = await fetcher<
      IGeneralData<IElectricityBillInquiryMutationResData>,
      IBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async electricityBillInfoMutation({
    payload,
    token,
  }: {
    payload: IBillPayload;
  } & ITokenBase): Promise<
    IGeneralData<IElectricityBillInfoInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.ELECTRICITY_BILL_INFO_INQUIRY;
    const data = await fetcher<
      IGeneralData<IElectricityBillInfoInquiryMutationResData>,
      IBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async gasBillMutation({
    payload,
    token,
  }: {
    payload: IGasBillPayload;
  } & ITokenBase): Promise<IGeneralData<IGasBillInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.GAS_BILL_INQUIRY;
    const data = await fetcher<
      IGeneralData<IGasBillInquiryMutationResData>,
      IGasBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async gasBillInfoMutation({
    payload,
    token,
  }: {
    payload: IGasBillInfoPayload;
  } & ITokenBase): Promise<IGeneralData<IGasBillInfoInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.GAS_BILL_INFO_INQUIRY;
    const data = await fetcher<
      IGeneralData<IGasBillInfoInquiryMutationResData>,
      IGasBillInfoPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async waterBillMutation({
    payload,
    token,
  }: {
    payload: IBillPayload;
  } & ITokenBase): Promise<IGeneralData<IWaterBillInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.WATER_BILL_INQUIRY;
    const data = await fetcher<
      IGeneralData<IWaterBillInquiryMutationResData>,
      IBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async waterBillInfoMutation({
    payload,
    token,
  }: {
    payload: IBillPayload;
  } & ITokenBase): Promise<IGeneralData<IWaterBillInfoInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.WATER_BILL_INFO_INQUIRY;
    const data = await fetcher<
      IGeneralData<IWaterBillInfoInquiryMutationResData>,
      IBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async electricityBillHistoryMutation({
    payload,
    token,
  }: {
    payload: IBillPayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawPurchasedElectricityHistoryInquiryResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.ELECTRICITY_BILL_HISTORY;
    const data = await fetcher<
      IGeneralData<IRawPurchasedElectricityHistoryInquiryResData>,
      IBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async electricityBillImageMutation({
    payload,
    token,
  }: {
    payload: IElectricityBillImagePayload;
  } & ITokenBase): Promise<
    IGeneralData<IRawPurchasedElectricityImageInquiryResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.ELECTRICITY_BILL_IMAGE;
    const data = await fetcher<
      IGeneralData<IRawPurchasedElectricityImageInquiryResData>,
      IBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async gasBillHistoryMutation({
    payload,
    token,
  }: {
    payload: IGasBillInfoPayload;
  } & ITokenBase): Promise<
    IGeneralData<IGasBillHistoryInquiryMutationResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.inquiry.GAS_BILL_HISTORY;
    const data = await fetcher<
      IGeneralData<IGasBillHistoryInquiryMutationResData>,
      IGasBillInfoPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async phoneBillImageMutation({
    payload,
    token,
  }: {
    payload: IBillPayload;
  } & ITokenBase): Promise<IGeneralData<IWaterBillInfoInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.PHONE_BILL_IMAGE_INQUIRY;
    const data = await fetcher<
      IGeneralData<IWaterBillInfoInquiryMutationResData>,
      IBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async mobileBillMciMutation({
    payload,
    token,
  }: {
    payload: IMobileBillPayload;
  } & ITokenBase): Promise<IGeneralData<IMtnBillInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.MOBILE_BILL_MCI_INQUIRY;
    const data = await fetcher<
      IGeneralData<IMtnBillInquiryMutationResData>,
      IMobileBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async mobileBillMtnMutation({
    payload,
    token,
  }: {
    payload: IMobileBillPayload;
  } & ITokenBase): Promise<IGeneralData<IMtnBillInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.MOBILE_BILL_MTN_INQUIRY;
    const data = await fetcher<
      IGeneralData<IMtnBillInquiryMutationResData>,
      IMobileBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async mobileBillRightelMutation({
    payload,
    token,
  }: {
    payload: IMobileBillPayload;
  } & ITokenBase): Promise<IGeneralData<IMtnBillInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.MOBILE_BILL_RIGHTEL_INQUIRY;
    const data = await fetcher<
      IGeneralData<IMtnBillInquiryMutationResData>,
      IMobileBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }

  async phoneBillMutation({
    payload,
    token,
  }: {
    payload: IMobileBillPayload;
  } & ITokenBase): Promise<IGeneralData<IMtnBillInquiryMutationResData>> {
    checkIsToken(token);
    const url = apiRoutes.inquiry.PHONE_BILL_INQUIRY;
    const data = await fetcher<
      IGeneralData<IMtnBillInquiryMutationResData>,
      IMobileBillPayload
    >({
      url,
      payload,
      token,
      method: "POST",
    });
    return data;
  }
}

export const inquiryClient = new InquiryClient(
  clientEnv.NEXT_PUBLIC_API_BASE_URL!,
);
